import { redirect, type ParsedLocation, type StaticDataRouteOption } from '@tanstack/react-router';
import type { Authenticator } from './authenticator';
import { safeReturnPath, SIGN_IN_PATH } from './return-path';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /**
     * Open without signing in (D-057). Every other route, and any path that matches
     * no route, needs a signed-in user.
     */
    readonly public?: boolean;
  }
}

interface GuardInput {
  readonly authenticator: Authenticator;
  readonly location: ParsedLocation;
  /** The routes matching the location, outermost first. */
  readonly matches: readonly { readonly staticData: StaticDataRouteOption }[];
}

/**
 * The root route's `beforeLoad`: sends anyone who isn't signed in from a page that
 * isn't public to the sign-in page, which brings them back afterwards. Only the
 * deepest route decides, so a public layout never opens the pages inside it.
 */
export async function requireSignIn({ authenticator, location, matches }: GuardInput) {
  await authenticator.ready();
  if (matches.at(-1)?.staticData.public === true || authenticator.currentUser() !== null) {
    return;
  }
  // `replace` keeps the refused page out of history, so Back doesn't bounce off it.
  throw redirect({ to: SIGN_IN_PATH, search: { redirect: location.href }, replace: true });
}

/** The sign-in route's `beforeLoad`: someone already signed in goes where they were heading. */
export function leaveSignInWhenSignedIn(authenticator: Authenticator, returnTo: unknown) {
  if (authenticator.currentUser() !== null) {
    throw redirect({ href: safeReturnPath(returnTo), replace: true });
  }
}
